/**
 * Whether something arriving is more of what is already here.
 *
 * The problem this exists to solve: every add used to mint a new row, so buying milk four
 * times left four milks with nothing to say which was which. To anyone using the app there is
 * one milk in the fridge, and buying more replenishes it.
 */

import type { InventoryItem, Level, LocationKind } from '../api/types';

/** What is arriving, in the terms a match is decided on. */
export interface Arrival {
  /** The grocery's slug. An item with no food reference matches nothing — see `restockable`. */
  foodRef: string | null;
  locationId: string;
  kind: LocationKind;
  /** Whether the household says this food does not go off — see `batched`. */
  isStaple: boolean;
}

/** Emptiest first, so a restock lands on the one the household is most likely replacing. */
const EMPTIEST_FIRST: readonly Level[] = ['out', 'low', 'plenty'];

/**
 * The item this arrival should replenish, or `null` if it should become a row of its own.
 *
 * Two things never match. A batch is not more of the same stock — see `batched`. And an item
 * carrying no food reference cannot be matched confidently at all; since the household's
 * grocery list arrived that is only reachable from a caller other than this one, because being
 * on that list is itself the claim that this is a thing they keep.
 */
export function restockable(items: InventoryItem[], arrival: Arrival): InventoryItem | null {
  if (arrival.foodRef === null || batched(arrival)) {
    return null;
  }

  const here = items
    .filter(
      (item) =>
        item.food_ref === arrival.foodRef &&
        item.location_id === arrival.locationId &&
        item.state !== 'gone',
    )
    .sort((left, right) => rank(left.level) - rank(right.level));

  return here[0] ?? null;
}

/**
 * Whether this arrival is a batch of its own rather than more of what is here.
 *
 * Only in a freezer, and only for food that goes off. The date a bag went in is the whole
 * reason freezer items are tracked, so a second bag of chicken is genuinely a second row.
 *
 * A staple is the case that used to be wrong. The household has said it does not go off, so
 * there is no date to keep apart and nothing for freezer amnesty to ask about — and the
 * freezer was therefore the one place where adding the same thing twice silently minted a
 * second row and never offered to restock. A fifth case of sparkling water is not five
 * batches; it is more of the one thing.
 */
function batched(arrival: Arrival): boolean {
  return arrival.kind === 'freezer' && !arrival.isStaple;
}

function rank(level: Level): number {
  return EMPTIEST_FIRST.indexOf(level);
}
